/**
 * EventsSection — the Events section index.
 *
 * Unified administrator (2026-08-05, operator-directed). Phase 1 folded Tickets,
 * Booking questions, Surveys and Coupons "inside Events" in the spec — but no
 * section navigation existed to hold them, so on a profile-mode rail (where the
 * served profile IS the menu) they were reachable by deep link only. Adding them
 * back as four more rail items took the admin rail to 20 rows / ~900px, which
 * pushes the governance tail below the fold. This is the fix: one Events entry,
 * nine surfaces behind it.
 *
 * Same pattern as WorkforceSection — deliberately, so the shell has ONE way of
 * expressing "a section with more surfaces than a rail can carry", not two.
 * Every row is capability-gated, so the screen is correct for a narrow persona:
 * an Event Coordinator sees Event Day and Calendar and nothing else.
 */
import { Screen, PageHeader } from "../../shell/chrome";
import { Icon, T } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";

interface Surface { route: string; label: string; labelKey: string; blurb: string; blurbKey: string; needs: string }
interface Group { id: string; label: string; labelKey: string; surfaces: Surface[] }

const GROUPS: Group[] = [
  { id: "running", label: "Running events", labelKey: "events.section.running", surfaces: [
    { route: "events",        needs: "events.read",     label: "All events",  labelKey: "events.section.all",       blurb: "Every event, draft to done",              blurbKey: "events.section.allBlurb" },
    { route: "eventday",      needs: "guests.checkin",  label: "Event Day",   labelKey: "events.section.eventday",  blurb: "Run the room — check-in and walk-ups",    blurbKey: "events.section.eventdayBlurb" },
    { route: "calendar.week", needs: "events.read",     label: "Calendar",    labelKey: "events.section.calendar",  blurb: "What is on, and when",                    blurbKey: "events.section.calendarBlurb" },
    { route: "approvals",     needs: "events.approve",  label: "Approvals",   labelKey: "events.section.approvals", blurb: "Submissions waiting on a decision",       blurbKey: "events.section.approvalsBlurb" },
  ]},
  { id: "setup", label: "Setup & content", labelKey: "events.section.setup", surfaces: [
    { route: "ticket-types",  needs: "events.write",    label: "Tickets",           labelKey: "events.section.tickets",  blurb: "Ticket types, tiers and inventory",   blurbKey: "events.section.ticketsBlurb" },
    { route: "questions",     needs: "questions.read",  label: "Booking questions", labelKey: "events.section.questions", blurb: "What you ask at registration",       blurbKey: "events.section.questionsBlurb" },
    { route: "surveys",       needs: "surveys.read",    label: "Surveys",           labelKey: "events.section.surveys",  blurb: "Post-event feedback forms",           blurbKey: "events.section.surveysBlurb" },
    { route: "promos",        needs: "promos.read",     label: "Coupons & Promos",  labelKey: "events.section.promos",   blurb: "Discount codes and offers",           blurbKey: "events.section.promosBlurb" },
    { route: "landing.pages", needs: "branding.read",   label: "Landing pages",     labelKey: "events.section.landing",  blurb: "The public page each event books from", blurbKey: "events.section.landingBlurb" },
    { route: "hub.pages",     needs: "events.read",     label: "Event hubs",        labelKey: "events.section.hubs",     blurb: "Public catalogs that list many events at once", blurbKey: "events.section.hubsBlurb" },
  ]},
];

export default function EventsSection() {
  const t = useT();
  const nav = useNav();
  const groups = GROUPS
    .map((g) => ({ ...g, surfaces: g.surfaces.filter((s) => can(s.needs)) }))
    .filter((g) => g.surfaces.length > 0);

  return (
    <Screen rail="ev-section">
      <PageHeader
        title={t("events.section.events", "Events")}
        subtitle={t("events.section.subtitle", "Everything that plans, fills and runs an event.")}
        breadcrumb={false}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 32px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 14, alignContent: "start" }}>
        {groups.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>
            {t("events.section.empty", "No event surfaces are available for your role.")}
          </div>
        ) : groups.map((g) => (
          <div key={g.id} style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" }}>
            <div style={{ padding: "11px 16px", borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700 }}>{t(g.labelKey, g.label)}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, background: T.n6, color: T.n1, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{g.surfaces.length}</span>
            </div>
            {g.surfaces.map((s, i) => (
              <button key={s.route} data-testid="ev-section-row" onClick={() => nav.go(s.route)}
                style={{ width: "100%", padding: "10px 16px", borderTop: i === 0 ? "none" : `1px solid ${T.n6}`, borderLeft: "none", borderRight: "none", borderBottom: "none", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: T.font, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = T.n6)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <span style={{ minWidth: 0 }}>
                  <b style={{ fontSize: 12.5, color: T.n1, display: "block" }}>{t(s.labelKey, s.label)}</b>
                  <span style={{ fontSize: 11, color: T.soft }}>{t(s.blurbKey, s.blurb)}</span>
                </span>
                <Icon.chev dir="right" size={14} />
              </button>
            ))}
          </div>
        ))}
      </div>
    </Screen>
  );
}
